import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Path, Rect } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import PrivateChatEmptyArt from '../../../assets/chat/private-chat-empty.png';
import type { ConversationSummary } from '../../../services/chatApi';
import { RootState } from '../../../store';
import { chatConversationsRequest } from '../../../store/chat/chatActions';
import { selectChatConversations } from '../../../store/chat/chatReducer';
import { GradientFill } from '../common/GradientFill';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import { ProfileActionButton } from '../Profile/ProfileActionButton';
import ChatAvatar from './ChatAvatar';
import { formatConversationStamp } from './chatFormat';
import {
	CHAT_FONT as FONT,
	CHAT_LIST_FILL,
	CHAT_UNREAD_FILL,
	chatColors as colors,
} from './chatTheme';

type Props = {
	/** The host's redesign scale: numbers here are in the 390-wide mobile design space. */
	scale: number;
	onOpenChat: (conversation: ConversationSummary) => void;
	/** The empty state's GO TO SOCIAL: where players are found to donate to. */
	onGoToSocial: () => void;
};

/** A desktop screen: the "VIP can only be sent on WEB" note. */
const MonitorIcon = ({ size, color }: { size: number; color: string }) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		<Rect x='2.5' y='3.5' width='19' height='13' rx='1.8' stroke={color} strokeWidth='2' />
		<Path d='M9 20.5h6M12 16.5v4' stroke={color} strokeWidth='2' strokeLinecap='round' />
	</Svg>
);

/** Two people: GO TO SOCIAL. */
const PeopleIcon = (color: string, size: number) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill={color}>
		<Circle cx='9' cy='8' r='3.6' />
		<Path d='M2.5 19.5c.4-3.6 3-5.8 6.5-5.8s6.1 2.2 6.5 5.8H2.5Z' />
		<Circle cx='16.8' cy='8.6' r='2.9' />
		<Path d='M16.4 13.2c3 .1 4.9 2.1 5.1 5.3h-4.4c-.2-2-1-3.8-2.4-5 .5-.2 1.1-.3 1.7-.3Z' />
	</Svg>
);

/**
 * The player's private chats, newest activity first: the Message Center's Private Chat tab. Each
 * row is the other player's avatar (with an unread count), their name, the last message and its
 * time; a row with unread messages gets the inbox's gold rim and red ramp. With no chats yet it
 * explains that a donation is what unlocks one.
 */
export const ConversationsList = ({ scale, onOpenChat, onGoToSocial }: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const styles = useMemo(() => createStyles(scale), [scale]);
	const myId = useSelector((s: RootState) => (s.auth?.user?.id ? String(s.auth.user.id) : ''));
	const { items, loaded, loading, errorKey } = useSelector(selectChatConversations);

	useEffect(() => {
		dispatch(chatConversationsRequest());
	}, [dispatch]);

	const preview = (conversation: ConversationSummary) => {
		const last = conversation.lastMessage;
		if (!last) {
			return t('T_SITE_CHAT_UNLOCKED_PREVIEW', { defaultValue: 'Chat unlocked. Say hello!' });
		}
		return last.senderId === myId
			? t('T_SITE_CHAT_YOU_PREFIX', { defaultValue: 'You: {{text}}', text: last.text })
			: last.text;
	};

	let status: React.ReactNode = null;
	if (!loaded && (loading || !errorKey)) {
		status = <ActivityIndicator color={colors.gold300} />;
	} else if (!loaded && errorKey) {
		status = (
			<View style={styles.statusStack}>
				<Text style={styles.status}>{errorKey}</Text>
				<Pressable
					accessibilityRole='button'
					onPress={() => dispatch(chatConversationsRequest())}
					style={styles.retryButton}
				>
					<Text style={styles.retryButtonText}>
						{t('T_SITE_CHAT_TRY_AGAIN', { defaultValue: 'Try again' })}
					</Text>
				</Pressable>
			</View>
		);
	} else if (!items.length) {
		// The mockup "Message Center / Private Chat / empty".
		status = (
			<View style={styles.empty}>
				<Image source={PrivateChatEmptyArt} resizeMode='contain' style={styles.emptyArt} />
				<Text style={styles.emptyTitle}>
					{t('T_SITE_CHAT_EMPTY_TITLE', { defaultValue: 'Start a private chat' }).toUpperCase()}
				</Text>
				<Text style={styles.emptyBody}>
					{t('T_SITE_CHAT_EMPTY_BODY', {
						defaultValue:
							'To start a private chat with another player, first send them a Chip package or a VIP period.',
					})}
				</Text>
				<View style={styles.emptyNote}>
					<MonitorIcon size={18 * scale} color={colors.gold300} />
					<Text style={styles.emptyNoteText}>
						{t('T_SITE_CHAT_EMPTY_VIP_WEB', { defaultValue: 'VIP can only be sent on WEB.' })}
					</Text>
				</View>
				<ProfileActionButton
					label={t('T_SITE_CHAT_GO_TO_SOCIAL', { defaultValue: 'Go to Social' })}
					icon={PeopleIcon}
					variant='gold'
					scale={scale}
					onPress={onGoToSocial}
					fill={false}
					style={styles.emptyAction}
				/>
			</View>
		);
	}

	return (
		<View style={styles.list}>
			<GradientFill stops={CHAT_LIST_FILL} />
			{status ? (
				<View style={styles.statusBox}>{status}</View>
			) : (
				<RedesignScrollView
					contentContainerStyle={styles.rows}
					indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
				>
					{items.map(conversation => {
						const unread = conversation.unreadCount > 0;
						const stampSource = conversation.lastMessage?.createdAt ?? conversation.unlockedAt;
						return (
							<Pressable
								key={conversation.id}
								accessibilityRole='button'
								accessibilityLabel={t('T_SITE_A11Y_OPEN_CHAT_WITH', {
									defaultValue: 'Open your chat with {{username}}',
									username: conversation.otherPlayer.username,
								})}
								onPress={() => onOpenChat(conversation)}
								style={[styles.row, unread && styles.rowUnread]}
							>
								<GradientFill stops={unread ? CHAT_UNREAD_FILL : CHAT_LIST_FILL} />
								<ChatAvatar
									avatarFileId={conversation.otherPlayer.avatarFileId}
									size={44 * scale}
									scale={scale}
									unread={conversation.unreadCount}
								/>
								<View style={styles.textCell}>
									<View style={styles.rowHeader}>
										<Text style={styles.name} numberOfLines={1}>
											{conversation.otherPlayer.username}
										</Text>
										{stampSource ? (
											<Text style={[styles.stamp, unread && styles.stampUnread]}>
												{formatConversationStamp(stampSource)}
											</Text>
										) : null}
									</View>
									<Text style={[styles.preview, unread && styles.previewUnread]} numberOfLines={1}>
										{preview(conversation)}
									</Text>
								</View>
							</Pressable>
						);
					})}
				</RedesignScrollView>
			)}
		</View>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const rim = Math.max(1, scale);
	return StyleSheet.create({
		list: {
			flex: 1,
			minHeight: 0,
			paddingHorizontal: s(6),
			paddingVertical: s(8),
			borderRadius: s(12),
			borderWidth: rim,
			borderColor: colors.listRim,
			overflow: 'hidden',
		},
		// 10 on the right keeps the rows clear of the scroll bar.
		rows: { gap: s(6), paddingTop: s(2), paddingRight: s(10), paddingBottom: s(2) },
		row: {
			minHeight: s(64),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(12),
			paddingHorizontal: s(10),
			paddingVertical: s(10),
			borderRadius: s(8),
			borderWidth: rim,
			borderColor: colors.rowBorder,
			overflow: 'hidden',
		},
		rowUnread: { borderColor: colors.buttonRim },
		textCell: { flex: 1, minWidth: 0, gap: s(3) },
		rowHeader: { flexDirection: 'row', alignItems: 'center', gap: s(8) },
		name: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(15),
			lineHeight: s(18),
			color: colors.gold50,
		},
		stamp: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(11),
			lineHeight: s(13),
			color: colors.muted,
		},
		stampUnread: { color: colors.gold200 },
		preview: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(12),
			lineHeight: s(15),
			color: colors.secondary,
		},
		previewUnread: { fontWeight: '700', color: colors.gold100 },
		statusBox: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: s(16) },
		statusStack: { alignItems: 'center', gap: s(12), maxWidth: s(280) },
		empty: { alignItems: 'center', maxWidth: s(320) },
		// The art is 310 by 260.
		emptyArt: { width: s(220), height: s(185) },
		emptyTitle: {
			marginTop: s(8),
			fontFamily: FONT,
			fontWeight: '800',
			fontSize: s(20),
			lineHeight: s(24),
			color: colors.gold50,
			textAlign: 'center',
		},
		emptyBody: {
			marginTop: s(8),
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(14),
			lineHeight: s(19),
			color: colors.secondary,
			textAlign: 'center',
		},
		emptyNote: {
			marginTop: s(14),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(8),
		},
		emptyNoteText: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(13),
			lineHeight: s(16),
			color: colors.secondary,
		},
		emptyAction: { alignSelf: 'stretch', marginTop: s(28), marginHorizontal: s(24) },
		status: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(14),
			lineHeight: s(18),
			color: colors.muted,
			textAlign: 'center',
		},
		retryButton: {
			paddingHorizontal: s(14),
			paddingVertical: s(6),
			borderRadius: s(10),
			borderWidth: rim,
			borderColor: colors.buttonRim,
			backgroundColor: colors.mineFill,
		},
		retryButtonText: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(13),
			lineHeight: s(16),
			color: colors.gold100,
		},
	});
};

export default ConversationsList;
